/* eslint-disable import/order */
import { PropsWithChildren, useCallback, useEffect } from 'react';

import {
    Channel,
    deviceUtils,
    EventType,
    Languages,
    loadingManager,
    Markets,
    useAppContext,
    useEnvContext,
    useEventBusEmit,
    useEventBusOn,
    useLocale,
    useMenuRouter,
    useRestContext,
} from 'scbk-services';
import {
    AstxKeypadProvider,
    Dialog,
    useAstxIsInstall,
    usePopup,
    VirtualKeypadProvider,
} from 'scbk-shared-module';

import { SVCSPD001_10TB } from 'scbk-servicing';

import { useAppInitContext } from '../providers/AppInitProvider';
import { useTranslation } from 'react-i18next';

/**
 * 인터넷뱅킹 앱 초기화 처리 컴포넌트
 * @param param0
 * @returns
 */
const InternetBankingInitializer: React.FC<PropsWithChildren> = ({ children }) => {
    useInternetBankingInitializer();

    const speedSearchPopup = usePopup('SVCSPD001_10TB');

    const { astxIsInstall } = useAstxIsInstall();

    const { goMenu } = useMenuRouter();

    const { t } = useTranslation();

    useEventBusOn({
        eventType: EventType.SpeedSearchPopupRequest,
        listener: async (...args: unknown[]) => {
            if (!args || args.length === 0) {
                return;
            }

            if (deviceUtils.isWin()) {
                loadingManager.open();

                if (await astxIsInstall()) {
                    loadingManager.close();
                    const arr = args as [React.MouseEvent<HTMLElement>];
                    const e = arr[0] as React.MouseEvent<HTMLElement>;
                    speedSearchPopup.open(e);
                    return;
                }

                loadingManager.close();
                await Dialog.alert({
                    content: t('InternetBankingInitializer.001', {
                        defaultValue:
                            "거래를 진행하기 위해서는 보안프로그램 설치가 필요합니다.<br/>보안프로그램을 설치하시겠습니까?<br/>('확인'을 누르시면 설치페이지로 이동합니다.)",
                    }),
                    onClick: () => {
                        goMenu('COMETC000'); // 통합설치
                    },
                });
            } else {
                const arr = args as [React.MouseEvent<HTMLElement>];
                const e = arr[0] as React.MouseEvent<HTMLElement>;
                speedSearchPopup.open(e);
            }
        },
    });
    return (
        <VirtualKeypadProvider>
            <SVCSPD001_10TB {...speedSearchPopup} />
            <AstxKeypadProvider>{children}</AstxKeypadProvider>
        </VirtualKeypadProvider>
    );
};

/**
 * 인터넷뱅킹 앱 초기화 hook
 * @returns
 */
const useInternetBankingInitializer = () => {
    const { getPreDefinedLocale, setLocale } = useLocale();
    const { getPreDefinedMarket } = useEnvContext();

    const { initialized, setInitialized } = useAppInitContext();
    const { setContext: setAppContext, updateContext: updateAppContext } = useAppContext();
    const { setContext: setRestContext, updateContext: updateRestContext } = useRestContext();

    const emit = useEventBusEmit();

    const defaultLocale = getPreDefinedLocale();
    const defaultMarket = getPreDefinedMarket(import.meta.env) || Markets.KR;

    /**
     * 언어(로케일)정보 초기화
     */
    const initLocale = useCallback(async () => {
        const nativeLocale = window.localStorage.getItem('locale');
        const locale = (nativeLocale ?? defaultLocale ?? Languages.KO) as Languages;

        setLocale(locale);
        window.localStorage.setItem('locale', locale);
    }, [defaultLocale, setLocale, updateAppContext, updateRestContext]);

    useEffect(() => {
        //이미 초기화가 되었다면 return;
        if (initialized) {
            console.log('### useInternetBankingInitializer already initialized!');
            return;
        }

        console.log('### useInternetBankingInitializer 초기화 처리 시작...');

        //rest api context update
        setRestContext({
            country: defaultMarket.toUpperCase(),
            channel: Channel.IB,
        });

        setAppContext({
            country: defaultMarket.toUpperCase(),
            channel: Channel.IB,
            initialized: true,
        });

        initLocale();

        setInitialized(true);

        //초기화 완료 이벤트
        emit(EventType.ScCoreReady);

        console.log('### useInternetBankingInitializer 초기화 처리 완료...');
    }, [initialized]);

    return { initialized };
};

export { InternetBankingInitializer };
